import { api } from './client';

/**
 * IELTS adaptive pool — Mock & Sprint (design: docs/ielts-ai-pool-design.md).
 *
 * Hosted by speaking-service; the gateway routes /api/v1/ielts/mock,
 * /api/v1/ielts/sprint and /api/v1/ielts/profile. The axios `api` client already
 * targets /api/v1, attaches the Bearer token and unwraps {status,message,data}.
 */

export type PoolSkill = 'reading' | 'listening' | 'writing' | 'speaking';

export type StudentQuestion = {
  id: string;
  ord: number;
  /** Последний номер у «выберите ДВА» (Questions 21–22), мок с 20.10.2026. */
  ord_to?: number;
  prompt: string;
  options?: string[];
  image_url?: string;
  /** Место вопроса в задании — к нему привязан пропуск {{N}} вёрстки (layout). */
  slot?: number;
};

export type StudentTask = {
  id: string;
  skill: PoolSkill;
  type: string;
  instruction: string;
  group_label: string;
  options?: string[]; // block-level (matching / bank / map)
  questions: StudentQuestion[];
  /** Вёрстка как на экзамене (формат т.4); разбор и проверка — lib/serverLayout.ts. */
  layout?: unknown;
};

export type ReadingBlock = {
  text: { id: string; title: string; body: string; word_count: number };
  tasks: StudentTask[];
};

export type ListeningBlock = {
  audio: { id: string; title: string; audio_url: string; duration_sec: number };
  tasks: StudentTask[];
};

export type WritingPrompt = {
  id: string;
  kind: string;
  subtype: string;
  prompt: string;
  image_url?: string;
};

export type SpeakingPrompt = {
  id: string;
  part: number;
  topic: string;
  cue_text?: string;
  questions?: string[];
};

export type MockContent = {
  listening: ListeningBlock[];
  reading: ReadingBlock[];
  writing: WritingPrompt[];
  speaking: SpeakingPrompt[];
  durations: Record<string, number>;
  sections: string[];
};

export type TypeTally = { correct: number; total: number; acc: number };

export type MockAttempt = {
  id: string;
  status: 'in_progress' | 'completed';
  remaining_seconds: number;
  current_section: string;
  current_index: number;
  answers: Record<string, unknown>;
  section_states: Record<string, unknown>;
  band_overall?: number;
  per_skill_bands?: Record<string, number>;
  per_type_accuracy?: Record<string, TypeTally>;
  strong_types?: string[];
  weak_types?: string[];
  order_plan?: Record<string, unknown>;
};

export type MockView = { attempt: MockAttempt; content: MockContent };

export type SkillProfile = {
  mock_done?: boolean;
  level?: string;
  per_skill_band?: Record<string, number>;
  per_type_accuracy?: Record<string, TypeTally>;
  strong_types?: string[];
  weak_types?: string[];
};

export type MockProgress = {
  remaining_seconds?: number;
  current_section?: string;
  current_index?: number;
  answers?: Record<string, unknown>;
  section_states?: Record<string, unknown>;
};

/** Опора вопроса в разборе: отрывок с выделением; Reading — абзац, Listening — реплика и время. */
export type MockReviewEvidence = {
  quote: string;
  context: string;
  hl_start: number;
  hl_end: number;
  paragraph?: string;
  paragraph_index?: number;
  speaker?: string;
  time_sec?: number;
  time_approx?: boolean;
};

export type MockReviewItem = {
  question_id: string;
  ord: number;
  ord_to?: number;
  type: string;
  prompt: string;
  options?: string[];
  student_answer: string;
  correct_answer: string;
  correct: boolean;
  points: number;
  max_points: number;
  explanation?: string;
  evidence?: MockReviewEvidence;
};

export type MockReviewGroup = {
  type: string;
  group_label: string;
  instruction: string;
  options?: string[];
  items: MockReviewItem[];
};

export type MockReviewBlock = {
  source_id: string;
  title: string;
  audio_url?: string;
  duration_sec?: number;
  passage?: number;
  part?: number;
  groups: MockReviewGroup[];
};

export type MockReviewSection = {
  skill: 'listening' | 'reading';
  band?: number;
  points: number;
  max_points: number;
  blocks: MockReviewBlock[];
};

/** Разбор последней сданной попытки (GET /ielts/mock/review, 20.10.2026). */
export type MockReview = {
  attempt_id: string;
  created_at: string;
  band_overall?: number;
  sections: MockReviewSection[] | null;
};

export const mockApi = {
  review: () => api.get<MockReview>('/ielts/mock/review').then((r) => r.data as unknown as MockReview),
  start: (level?: string) =>
    api.get<MockView>('/ielts/mock', { params: level ? { level } : undefined }).then((r) => r.data as unknown as MockView),
  saveProgress: (id: string, p: MockProgress) =>
    api.put(`/ielts/mock/${id}/progress`, p).then((r) => r.data),
  submit: (id: string, answers: Record<string, unknown>) =>
    // У мока вопросов ещё больше, чем у спринта, — таймаут нужен тот же.
    api.post<MockAttempt>(`/ielts/mock/${id}/submit`, { answers }, { timeout: 180_000 })
      .then((r) => r.data as unknown as MockAttempt),
  profile: () => api.get<SkillProfile>('/ielts/profile').then((r) => r.data as unknown as SkillProfile),
};

export type SprintView = { attempt: { id: string; status: string; composition: Record<string, unknown> }; content: MockContent };

export const sprintApi = {
  start: () => api.get<SprintView>('/ielts/sprint').then((r) => r.data as unknown as SprintView),
  saveProgress: (id: string, answers: Record<string, unknown>) =>
    api.put(`/ielts/sprint/${id}/progress`, { answers }).then((r) => r.data),
  submit: (id: string, answers: Record<string, unknown>) =>
    // Проверку письма и речи делает AI-экзаменатор, и это занимает время:
    // тридцати секунд общего таймаута не хватает. Замер 13.08.2026 показал
    // сдачу длиной 5 минут — ученик видел ошибку, хотя всё было посчитано
    // и записано в базу. Объяснения к вопросам с тех пор ушли в фон, но
    // сама проверка быстрой не станет, поэтому здесь свой запас.
    api.post(`/ielts/sprint/${id}/submit`, { answers }, { timeout: 180_000 }).then((r) => r.data),
};

/* ─────────────────  admin pool management  ───────────────── */

export type PoolText = {
  id: string;
  title: string;
  body: string;
  word_count: number;
  cefr_level: string;
  source: string;
  active: boolean;
  created_at?: string;
};

export type PoolAudio = {
  id: string;
  title: string;
  audio_url: string;
  transcript?: string;
  duration_sec: number;
  cefr_level: string;
  source: string;
  active: boolean;
  created_at?: string;
};

export type PoolQuestion = {
  id: string;
  ord: number;
  prompt: string;
  options?: string[];
  answer_key: string;
  synonyms?: string[];
};

export type PoolTask = {
  id: string;
  skill: string;
  type: string;
  instruction: string;
  group_label: string;
  options?: string[];
  cefr_level: string;
  source: string;
  questions: PoolQuestion[];
};

export type GenReadingBody = { level?: string; types?: string[]; count?: number };

export const adminPoolApi = {
  generateReading: (b: GenReadingBody) => api.post('/admin/ielts/pool/generate/reading', b).then((r) => r.data),
  generateListening: (b: GenReadingBody) => api.post('/admin/ielts/pool/generate/listening', b).then((r) => r.data),
  generateWriting: (b: { kind?: string; subtype?: string; level?: string; count?: number }) =>
    api.post('/admin/ielts/pool/generate/writing', b).then((r) => r.data),
  generateSpeaking: (b: { level?: string }) => api.post('/admin/ielts/pool/generate/speaking', b).then((r) => r.data),
  listTexts: (level?: string) =>
    api.get('/admin/ielts/pool/texts', { params: level ? { level } : undefined }).then((r) => (r.data as unknown as PoolText[]) ?? []),
  textTasks: (id: string) =>
    api.get(`/admin/ielts/pool/texts/${id}/tasks`).then((r) => (r.data as unknown as PoolTask[]) ?? []),
  listAudios: (level?: string) =>
    api.get('/admin/ielts/pool/audios', { params: level ? { level } : undefined }).then((r) => (r.data as unknown as PoolAudio[]) ?? []),
  audioTasks: (id: string) =>
    api.get(`/admin/ielts/pool/audios/${id}/tasks`).then((r) => (r.data as unknown as PoolTask[]) ?? []),
  listWriting: () => api.get('/admin/ielts/pool/writing').then((r) => (r.data as unknown as WritingPrompt[]) ?? []),
  listSpeaking: () => api.get('/admin/ielts/pool/speaking').then((r) => (r.data as unknown as SpeakingPrompt[]) ?? []),
  setTextActive: (id: string, active: boolean) =>
    api.post(`/admin/ielts/pool/texts/${id}/active`, null, { params: { active } }).then((r) => r.data),
  setAudioActive: (id: string, active: boolean) =>
    api.post(`/admin/ielts/pool/audios/${id}/active`, null, { params: { active } }).then((r) => r.data),
};

/** All AI-generatable reading types (map_labeling excluded — needs an image). */
export const READING_TYPES = [
  'mc_single',
  'mc_multi',
  'true_false_ng',
  'yes_no_ng',
  'matching_info',
  'matching_headings',
  'matching_features',
  'matching_sentence_endings',
  'sentence_completion',
  'summary_completion',
  'summary_completion_bank',
  'note_completion',
  'table_completion',
  'flowchart_completion',
  'diagram_label',
  'short_answer',
] as const;

export const LISTENING_TYPES = ['form_completion', 'note_completion', 'matching', 'mc_single', 'sentence_completion'] as const;

export const CEFR_LEVELS = ['A2', 'B1', 'B2', 'C1', 'C2'] as const;

/** Human-readable RU label for a task type (used in stats + task headers). */
// Названия типов заданий — ключи переводов: их видит ученик на результатах
// мок-теста и в разборе ответов, поэтому текст живёт в locales/taskTypes.
export const TYPE_LABEL_KEY: Record<string, string> = {
  mc_single: 'taskTypes.mc_single',
  mc_multi: 'taskTypes.mc_multi',
  true_false_ng: 'taskTypes.true_false_ng',
  yes_no_ng: 'taskTypes.yes_no_ng',
  matching_info: 'taskTypes.matching_info',
  matching_headings: 'taskTypes.matching_headings',
  matching_features: 'taskTypes.matching_features',
  matching_sentence_endings: 'taskTypes.matching_sentence_endings',
  sentence_completion: 'taskTypes.sentence_completion',
  summary_completion: 'taskTypes.summary_completion',
  summary_completion_bank: 'taskTypes.summary_completion_bank',
  note_completion: 'taskTypes.note_completion',
  table_completion: 'taskTypes.table_completion',
  flowchart_completion: 'taskTypes.flowchart_completion',
  diagram_label: 'taskTypes.diagram_label',
  short_answer: 'taskTypes.short_answer',
  map_labeling: 'taskTypes.map_labeling',
  form_completion: 'taskTypes.form_completion',
  matching: 'taskTypes.matching',
};

export const SKILL_LABEL_RU: Record<string, string> = {
  reading: 'Reading',
  listening: 'Listening',
  writing: 'Writing',
  speaking: 'Speaking',
};

/** Types that answer with the exact TEXT of a block-level option (a <select>). */
export function hasBlockOptions(type: string): boolean {
  return [
    'matching_info',
    'matching_headings',
    'matching_features',
    'matching_sentence_endings',
    'summary_completion_bank',
    'map_labeling',
    'matching',
  ].includes(type);
}

export function isCompletion(type: string): boolean {
  return [
    'sentence_completion',
    'summary_completion',
    'note_completion',
    'table_completion',
    'flowchart_completion',
    'diagram_label',
    'short_answer',
    'form_completion',
  ].includes(type);
}

/**
 * Вопрос с сомнительным ключом. При генерации разбора модель вывела из текста
 * другой ответ, чем записан в ключе. Автоматически ключ НЕ меняется: модель
 * ошибается не реже генератора, и подмена верного ключа на неверный хуже,
 * чем сомнительный ключ. Решает человек.
 */
export type QuestionForReview = {
  id: string;
  prompt: string;
  answer_key: string;
  note: string;
  type: string;
  skill: string;
  options: string;
  source: string;
  title: string;
};

export const poolReviewApi = {
  list: () =>
    api
      .get<{ items: QuestionForReview[]; total: number }>('/admin/ielts/pool/review')
      .then((r) => (r.data as unknown as { items: QuestionForReview[]; total: number })),

  /** Пустой answerKey — «ключ верен». Заданный — «заменить на этот». */
  resolve: (id: string, answerKey?: string) =>
    api.post(`/admin/ielts/pool/review/${id}/resolve`, { answer_key: answerKey ?? '' }).then((r) => r.data),
};
